import React from "react";
import { Handle, Position, useNodeConnections } from "@xyflow/react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { edgeLabelFor } from "@/util/callFlowNodes";
import { useFlowBuilder } from "../components/FlowBuilderContext";

interface BranchHandlesProps {
  nodeId: string;
  edgeKeys: string[];
}

interface BranchHandleProps {
  nodeId: string;
  edgeKey?: string;
  isHorizontal: boolean;
}

export default function BranchHandles({ nodeId, edgeKeys }: BranchHandlesProps) {
  const { orientation } = useFlowBuilder();
  const isHorizontal = orientation === "horizontal";

  if (edgeKeys.length === 0) {
    return null;
  }

  return (
    <div className={cn("border-t", isHorizontal ? "flex flex-col divide-y" : "flex divide-x")}>
      {edgeKeys.map((edgeKey) => (
        <BranchHandle key={edgeKey} nodeId={nodeId} edgeKey={edgeKey} isHorizontal={isHorizontal} />
      ))}
    </div>
  );
}

export function BranchHandle({ nodeId, edgeKey, isHorizontal }: BranchHandleProps) {
  const { addLinkedNode } = useFlowBuilder();
  const connections = useNodeConnections({ id: nodeId, handleType: "source", handleId: edgeKey });
  const isConnected = connections.length > 0;

  return (
    <div
      className={cn(
        "relative flex items-center gap-2 px-4 py-3",
        isHorizontal ? "justify-between" : "min-w-0 flex-1 justify-center",
      )}
    >
      <span className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {edgeKey ? edgeLabelFor(edgeKey) : "Next"}
      </span>
      {!isConnected && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-6 w-6 shrink-0 rounded-full"
          title="Add the next step"
          onClick={() => addLinkedNode(nodeId, edgeKey)}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      )}
      <Handle id={edgeKey} type="source" position={isHorizontal ? Position.Right : Position.Bottom} />
    </div>
  );
}
